import * as JSPM4 from '../../vendor/jsprintmanager-v4/JSPrintManager';
import * as JSPM9 from '../../vendor/jsprintmanager-v9/JSPrintManager';
import { getLocalStorageItems, setLocalStorageItems } from '../scan/utils';
import ServiceHandler from '../../services/service-handler';
import { ZebraZPLLabel } from './zebralabel';
import moment from 'moment';
import { API_PATH, getApiBaseUrl } from '../../api-paths';
import PrinterService, {
    JspmConfiguration,
} from '../../services/printer-service/printer-service';

export type LabelType = 'box' | 'pallet' | 'bin';

export class PrintManager {
    private static activeClient: any = JSPM4;
    private static startedLicenseUrl = '';
    private static configurationsRequest: Promise<JspmConfiguration[]> | null =
        null;

    private static reclaimCenterId(reclaimCenterId: unknown): string {
        if (typeof reclaimCenterId === 'string') {
            return reclaimCenterId.trim();
        }
        if (typeof reclaimCenterId === 'number') {
            return String(reclaimCenterId);
        }
        return '';
    }

    private static isVersion9(keyVersion: string): boolean {
        return keyVersion.trim().toUpperCase() === 'NEW_V2';
    }

    private static configurations(): Promise<JspmConfiguration[]> {
        if (!PrintManager.configurationsRequest) {
            PrintManager.configurationsRequest = new PrinterService()
                .getJspmConfigurations()
                .catch(() => {
                    PrintManager.configurationsRequest = null;
                    return [];
                });
        }
        return PrintManager.configurationsRequest;
    }

    private static clientFor(
        reclaimCenterId: unknown,
        configurations: JspmConfiguration[]
    ) {
        console.log('Configurations:', configurations);
        const id = PrintManager.reclaimCenterId(reclaimCenterId);
        const match = configurations.find(
            (item) => item.reclaimCenterId === id
        );
        return match && PrintManager.isVersion9(match.keyVersion)
            ? JSPM9
            : JSPM4;
    }

    private static licenseUrlFor(reclaimCenterId: unknown): string {
        const licenseUrl = getApiBaseUrl() + API_PATH.PRINTER_SETTINGS_JSPM;
        const hasReclaimCenterId =
            (typeof reclaimCenterId === 'string' &&
                reclaimCenterId.trim() !== '') ||
            typeof reclaimCenterId === 'number';
        return hasReclaimCenterId
            ? `${licenseUrl}?reclaimCenterId=${encodeURIComponent(
                  String(reclaimCenterId)
              )}`
            : licenseUrl;
    }

    public static start(): Promise<void> {
        return PrintManager.configurations().then((configurations) => {
            const reclaimCenterId = getLocalStorageItems('reclaimCenterId');
            return PrintManager.openClient(reclaimCenterId, configurations);
        });
    }

    private static openClient(
        reclaimCenterId: unknown,
        configurations: JspmConfiguration[]
    ): Promise<void> {
        const nextClient = PrintManager.clientFor(
            reclaimCenterId,
            configurations
        );
        const licenseUrl = PrintManager.licenseUrlFor(reclaimCenterId);
        const sameClientAlreadyOpen =
            PrintManager.activeClient === nextClient &&
            PrintManager.startedLicenseUrl === licenseUrl &&
            PrintManager.activeClient.JSPrintManager.websocket_status ===
                PrintManager.activeClient.WSStatus.Open;
        if (sameClientAlreadyOpen) {
            return Promise.resolve();
        }
        if (
            PrintManager.activeClient !== nextClient &&
            PrintManager.startedLicenseUrl
        ) {
            PrintManager.activeClient.JSPrintManager.stop();
        }
        PrintManager.activeClient = nextClient;
        PrintManager.activeClient.JSPrintManager.license_url = licenseUrl;
        PrintManager.activeClient.JSPrintManager.auto_reconnect = true;
        PrintManager.startedLicenseUrl = licenseUrl;
        return PrintManager.activeClient.JSPrintManager.start();
    }

    public static webSocketStatus(): boolean {
        const client = PrintManager.activeClient;
        return client.JSPrintManager.websocket_status === client.WSStatus.Open;
    }

    public static printerName(type: LabelType): string {
        let localStoragePrinterSettings =
            getLocalStorageItems('printerSettings');
        if (!localStoragePrinterSettings) {
            //Back up in case not there in local storage.
            ServiceHandler.PrinterService.getPrinterSettings(
                getLocalStorageItems('reclaimCenterId'),
                getLocalStorageItems('scanSettings').scanStation
            ).then((printerSettings) => {
                setLocalStorageItems('printerSettings', printerSettings);
                localStoragePrinterSettings = printerSettings;
            });
        }
        if (localStoragePrinterSettings) {
            switch (type) {
                case 'box':
                    return localStoragePrinterSettings.boxLabelPrinterName;
                case 'bin':
                    return localStoragePrinterSettings.boxLabelPrinterName;
                case 'pallet':
                    return localStoragePrinterSettings.palletLabelPrinterName;
                default:
                    return 'default';
            }
        } else {
            return 'default';
        }
    }

    public static printLabel(
        payload: any,
        printerType: LabelType,
        loggedInUserId?: any,
        closedBoxes?: any
    ) {
        if (!PrintManager.webSocketStatus()) {
            return;
        }
        const clientPrintJob = new PrintManager.activeClient.ClientPrintJob();
        const printerName = PrintManager.printerName(printerType);
        if (printerType === 'box' || printerType === 'bin') {
            clientPrintJob.printerCommands = ZebraZPLLabel.getBoxLabel(
                PrintManager.getBoxPrintLabelRequest(
                    payload,
                    printerType,
                    loggedInUserId,
                    closedBoxes
                )
            );
        }
        if (printerType === 'pallet') {
            clientPrintJob.printerCommands =
                ZebraZPLLabel.getPalletLabel(payload);
        }
        clientPrintJob.clientPrinter =
            new PrintManager.activeClient.InstalledPrinter(printerName);
        PrintManager.activeClient.JSPrintManager.getPrinters().then(
            (printersList: any) => {
                if (printersList) {
                    const printersArray = printersList as string[];
                    const validPrinter = printersArray.includes(printerName);
                    if (validPrinter) {
                        clientPrintJob
                            .sendToClient()
                            .then(() => {})
                            .catch();
                    }
                }
            }
        );
    }

    public static getBoxPrintLabelRequest(
        payload: any,
        printerType: LabelType,
        loggedInUserId: any,
        closedBoxes: any
    ) {
        let boxId;
        if (printerType === 'box') {
            boxId = payload.outboundBoxId;
        } else if (printerType === 'bin') {
            boxId = payload.binId;
        }
        return {
            scanMode: printerType === 'bin' ? 'Bin' : 'Box',
            boxId: boxId || '',
            date: moment(payload?.lastModifiedDate).format('L'),
            palletLocation: payload?.palletLocation || '',
            currentQuantity: payload?.currentQuantity || '',
            warehouseLocation: payload.warehouseLocation || '',
            closedBoxes: closedBoxes || '',
            userId: loggedInUserId || '',
        };
    }
}
